import { generatePath, matchPath } from 'react-router';
import { all, call, put, select, takeLatest, take } from 'redux-saga/effects';

import {
  FETCH_DEALER_FAILURE,
  FETCH_DEALER_SUCCESS,
  SELECT_DEALER,
} from 'constants/ActionTypes';
import { PROJECTION } from 'constants/ApiConstants';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { PATH } from 'constants/RoutesConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage, waitForApplicationReady } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';
import { selectDealer } from 'modules/dealers/actions';
import { fetchGroup, selectGroup } from 'modules/groups/actions';
import {
  FETCH_GROUP_FAILURE,
  FETCH_GROUP_SUCCESS,
  SELECT_GROUP,
} from 'modules/groups/actionTypes';
import { getSelectedGroupId } from 'modules/groups/selectors';
import * as actions from 'modules/payers/actions';
import * as types from 'modules/payers/actionTypes';
import { PAYER_DASHBOARD_SOURCE, PAYER_DETAILS_SOURCE } from 'modules/payers/constants';
import { getBaseRequestParams } from 'modules/payers/selectors';
import { navigate } from 'modules/router/navigate';
import { getSelectedDealerGroupId } from 'selectors/dealerSelectors';
import { getCurrentLocation } from 'selectors/router';

// Wait until the right group is selected
function* checkGroupDealer() {
  const selectedDealerGroupId = yield select(getSelectedDealerGroupId);
  const groupId = yield select(getSelectedGroupId);

  if (selectedDealerGroupId !== groupId) {
    yield take(SELECT_GROUP);
  }
}

function* refreshDashboard() {
  yield call(checkGroupDealer);

  const { page: _, ...params } = yield select(getBaseRequestParams);
  yield put(actions.fetchPayers({ ...params, page: 0 }, { source: PAYER_DASHBOARD_SOURCE }));
}

function* afterCreateWorker({ payload: payer }) {
  const location = yield select(getCurrentLocation);
  const isDealerPage = Boolean(matchPath(PATH.PAYERS_DETAILS.path, location));
  const destination = isDealerPage ? PATH.PAYERS_DETAILS : PATH.GROUP_PAYERS_DETAILS;
  yield call(navigate, generatePath(destination.path, { payerId: payer.id }), { replace: true });
}

const onFilterChange = ({ type }) =>
  [
    SELECT_DEALER,
    SELECT_GROUP,
    types.SET_PAYERS_DASHBOARD_FILTER,
    types.CLEAR_PAYERS_DASHBOARD_FILTER,
    types.REMOVE_PAYERS_DASHBOARD_FILTER,
  ].includes(type);

function* onDashboardPageWorker() {
  yield call(refreshDashboard);
  yield call(waitForApplicationReady, true);
  yield takeLatest(onFilterChange, refreshDashboard);
}

/**
 * @param {import('./types').Payer} payer
 */
function* dealerDetailsCallback(payer) {
  yield put(selectDealer(payer.dealerId));
  yield take([FETCH_DEALER_SUCCESS, FETCH_DEALER_FAILURE]);
}

/**
 * @param {import('./types').Payer} payer
 */
function* groupDetailsCallback(payer) {
  const groupId = yield select(getSelectedGroupId);
  if (payer && groupId !== payer.groupId) {
    yield put(selectGroup(groupId, { projection: PROJECTION.CONFIG }));
    yield take([FETCH_GROUP_SUCCESS, FETCH_GROUP_FAILURE]);
  } else {
    yield putAndWait(fetchGroup(groupId, { projection: PROJECTION.CONFIG }));
  }
}

/**
 * @param {string} path
 * @param {boolean} isDealer
 */
function* onDetailsPageWorker(path, isDealer) {
  const location = yield select(getCurrentLocation);
  const matches = matchPath(path, location);
  const callbackFn = isDealer ? dealerDetailsCallback : groupDetailsCallback;

  yield call(waitForApplicationReady, true);
  if (matches.params.payerId !== NEW_ENTITY_ID) {
    const { error, payload: payer } = yield putAndWait(
      actions.fetchPayer(matches.params.payerId, { source: PAYER_DETAILS_SOURCE })
    );
    if (!error) {
      yield call(callbackFn, payer);
    }
  } else {
    yield call(callbackFn);
  }
  yield put(actions.setIsReady());
}

function* onDealerDetailsPageWorker() {
  yield call(onDetailsPageWorker, PATH.PAYERS_DETAILS.path, true);
}

function* onGroupDetailsPageWorker() {
  yield call(onDetailsPageWorker, PATH.GROUP_PAYERS_DETAILS.path, false);
}

const requestsConfig = {
  [types.DELETE_PAYER]: {
    callbacks: {
      success: (_, meta, { payload: { payerId } }) => actions.deletePayerSuccess(payerId, meta),
      failure: ({ payload: error }, meta, { payload: { payerId } }) =>
        actions.deletePayerFailure(error, { ...meta, entityId: payerId }),
    },
    notifications: {
      success: {
        id: 'page.payers.actions.delete.success',
        defaultMessage: 'The payer has been deleted with success',
      },
      failure: {
        id: 'page.payers.actions.delete.failure',
        defaultMessage: 'An error occurred during the payer deletion',
      },
    },
  },
  [types.PUT_PAYER]: {
    notifications: {
      success: {
        id: 'page.payers.actions.update.success',
        defaultMessage: 'The payer has been updated with success',
      },
      failure: {
        id: 'page.payers.actions.update.failure',
        defaultMessage: 'An error occurred during the payer update',
      },
    },
  },
  [types.POST_PAYER]: {
    notifications: {
      success: {
        id: 'page.payers.actions.create.success',
        defaultMessage: 'The payer has been created with success',
      },
      failure: {
        id: 'page.payers.actions.create.failure',
        defaultMessage: 'An error occurred during the payer creation',
      },
    },
  },
};

export default function* PayerManagementSagas() {
  // Actions
  yield scopeToPage([PATH.PAYERS_DETAILS], onDealerDetailsPageWorker);
  yield scopeToPage([PATH.GROUP_PAYERS_DETAILS], onGroupDetailsPageWorker);
  yield scopeToPage([PATH.PAYERS, PATH.GROUP_PAYERS], onDashboardPageWorker);

  yield takeLatest(types.POST_PAYER_SUCCESS, afterCreateWorker);

  // Request
  yield all(simpleRequestsLoader(requestsConfig));
}
